import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Pencil, Trash2, X } from "lucide-react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { api, fmtErr } from "../../lib/api";
import { useTheme } from "../../components/ThemeToggle";

function PostForm({ initial, onDone, onCancel }) {
  const [dark] = useTheme();
  const [meta, setMeta] = useState({
    title: initial?.title || "", slug: initial?.slug || "", excerpt: initial?.excerpt || "",
    tags: (initial?.tags || []).join(", "), author: initial?.author || "Tendly Team",
    visuals_by: initial?.visuals_by || "", cover_image: initial?.cover_image || "",
  });
  const [busy, setBusy] = useState(false);
  const coverRef = useRef(null);
  const [coverBusy, setCoverBusy] = useState(false);

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image exceeds the 5 MB limit");
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/content/blog-image", fd);
      setMeta((m) => ({ ...m, cover_image: r.data.url }));
      toast.success("Cover image set");
    } catch (e) { toast.error(fmtErr(e)); }
    setCoverBusy(false);
  };
  const editor = useCreateBlockNote({
    uploadFile: async (file) => {
      if (file.size > 5 * 1024 * 1024) { toast.error("Image exceeds the 5 MB limit"); throw new Error("file too large"); }
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/content/blog-image", fd);
      return r.data.url;
    },
  });
  const loaded = useRef(false);
  useEffect(() => {
    if (initial?.body && !loaded.current) {
      loaded.current = true;
      editor.tryParseHTMLToBlocks(initial.body)
        .then((blocks) => editor.replaceBlocks(editor.document, blocks))
        .catch(() => {});
    }
  }, [editor, initial]);

  const save = async () => {
    if (!meta.title.trim() || !meta.slug.trim()) return toast.error("Title and slug are required");
    setBusy(true);
    try {
      const html = await editor.blocksToHTMLLossy();
      const payload = { ...meta, body: html, tags: meta.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (initial?.id) await api.put(`/admin/content/blog_posts/${initial.id}`, payload);
      else await api.post("/admin/content/blog_posts", payload);
      toast.success(initial?.id ? "Post updated" : "Post published");
      onDone();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — sign in again in a NEW tab (tendlyrentals.com/staff), then come back and click publish again. Your draft on this page is safe.", { duration: 12000 });
      } else {
        toast.error(fmtErr(e));
      }
    }
    setBusy(false);
  };

  const inputCls = "rounded-xl border border-input bg-background px-3 py-2 text-sm";
  return (
    <div className="mt-4 grid gap-2" data-testid="post-form">
      <input data-testid="post-title" placeholder="Title" value={meta.title}
        onChange={(e) => setMeta({ ...meta, title: e.target.value, slug: initial?.id ? meta.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") })}
        className={inputCls} aria-label="Post title" />
      <input data-testid="post-slug" placeholder="slug" value={meta.slug} onChange={(e) => setMeta({ ...meta, slug: e.target.value })} className={`${inputCls} font-mono`} aria-label="Post slug" />
      <input data-testid="post-excerpt" placeholder="Excerpt" value={meta.excerpt} onChange={(e) => setMeta({ ...meta, excerpt: e.target.value })} className={inputCls} aria-label="Post excerpt" />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="post-author" placeholder="Written by" value={meta.author} onChange={(e) => setMeta({ ...meta, author: e.target.value })} className={inputCls} aria-label="Written by" />
        <input data-testid="post-visuals" placeholder="Visuals by (optional)" value={meta.visuals_by} onChange={(e) => setMeta({ ...meta, visuals_by: e.target.value })} className={inputCls} aria-label="Visuals by" />
      </div>
      <input data-testid="post-tags" placeholder="Tags (comma separated)" value={meta.tags} onChange={(e) => setMeta({ ...meta, tags: e.target.value })} className={inputCls} aria-label="Post tags" />
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="post-cover-input"
          onChange={(e) => { uploadCover(e.target.files[0]); e.target.value = ""; }} />
        <button type="button" data-testid="post-cover-btn" onClick={() => coverRef.current?.click()} disabled={coverBusy}
          className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
          <ImagePlus className="w-3.5 h-3.5" /> {coverBusy ? "Uploading…" : meta.cover_image ? "Change cover image" : "Add cover image"}
        </button>
        {meta.cover_image && (
          <span className="flex items-center gap-2">
            <img src={meta.cover_image} alt="Cover preview" data-testid="post-cover-preview" className="h-10 w-16 object-cover rounded-lg border border-border" />
            <button type="button" data-testid="post-cover-remove" onClick={() => setMeta((m) => ({ ...m, cover_image: "" }))} className="text-coral-600" aria-label="Remove cover"><X className="w-4 h-4" /></button>
          </span>
        )}
        <span className="text-xs text-muted-foreground">Shown on the blog card and at the top of the article</span>
      </div>
      <div className="rounded-xl border border-input bg-background overflow-hidden" data-testid="post-body-editor">
        <BlockNoteView editor={editor} theme={dark ? "dark" : "light"} />
      </div>
      <p className="text-xs text-muted-foreground">Visual editor: select text for bold / italic / headers, type "/" for blocks, drag or paste images straight into the body.</p>
      <div className="flex gap-2">
        <button data-testid="post-save" onClick={save} disabled={busy}
          className="flex-1 rounded-full bg-primary text-primary-foreground py-2 text-sm font-semibold disabled:opacity-50">
          {busy ? "Saving…" : initial?.id ? "Save changes" : "Publish post"}
        </button>
        <button data-testid="post-cancel" onClick={onCancel} className="rounded-full border border-border px-4 py-2 text-sm inline-flex items-center gap-1"><X className="w-4 h-4" /> Cancel</button>
      </div>
    </div>
  );
}

export default function Content() {
  const [content, setContent] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [newT, setNewT] = useState({ name: "", area: "", text: "", rating: 5 });
  const [newF, setNewF] = useState({ q: "", a: "" });
  const [newP, setNewP] = useState(null); // null = form hidden, {} = new post, {id,...} = editing

  const load = () => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveContent = async (key) => {
    try { await api.put(`/admin/content/${key}`, { value: content[key] }); toast.success("Saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const add = async (kind, payload, reset) => {
    try { await api.post(`/admin/content/${kind}`, payload); reset(); load(); toast.success("Added"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const del = async (kind, id) => {
    try { await api.delete(`/admin/content/${kind}/${id}`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="content-page" className="max-w-4xl">
      <h1 className="font-heading font-bold text-2xl tracking-tight">Content management</h1>
      <p className="text-sm text-muted-foreground mb-8">Homepage copy, testimonials, FAQs, and blog posts.</p>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-semibold text-lg">Homepage</h2>
        {[["hero_title", "Hero title"], ["hero_subtitle", "Hero subtitle"], ["tagline", "Tagline"], ["announcement", "Announcement"]].map(([key, label]) => (
          <div key={key} className="mt-4">
            <label className="block text-xs font-semibold mb-1.5" htmlFor={`content-${key}`}>{label}</label>
            <div className="flex gap-2">
              <input id={`content-${key}`} data-testid={`content-${key}`} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })}
                className="flex-1 rounded-xl border border-input bg-background px-4 py-2 text-sm" />
              <button data-testid={`content-save-${key}`} onClick={() => saveContent(key)} className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold">Save</button>
            </div>
          </div>
        ))}
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-semibold text-lg">Testimonials <span className="text-xs font-normal text-muted-foreground">(samples until real ones are collected)</span></h2>
        <ul className="mt-3 space-y-2">
          {testimonials.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 rounded-xl bg-secondary px-4 py-2.5 text-sm">
              <span><strong>{t.name}</strong> ({t.area}) — {t.text.slice(0, 80)}…</span>
              <button data-testid={`testimonial-delete-${t.id}`} onClick={() => del("testimonials", t.id)} aria-label="Delete testimonial" className="text-coral-600"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <input data-testid="new-testimonial-name" placeholder="Name" value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} className="rounded-xl border border-input bg-background px-3 py-2 text-sm" aria-label="Testimonial name" />
          <input data-testid="new-testimonial-area" placeholder="Area" value={newT.area} onChange={(e) => setNewT({ ...newT, area: e.target.value })} className="rounded-xl border border-input bg-background px-3 py-2 text-sm" aria-label="Testimonial area" />
          <textarea data-testid="new-testimonial-text" placeholder="Quote" value={newT.text} onChange={(e) => setNewT({ ...newT, text: e.target.value })} className="col-span-2 rounded-xl border border-input bg-background px-3 py-2 text-sm" rows={2} aria-label="Testimonial quote" />
          <button data-testid="new-testimonial-add" onClick={() => add("testimonials", { ...newT, sample: true }, () => setNewT({ name: "", area: "", text: "", rating: 5 }))}
            className="col-span-2 rounded-full bg-primary text-primary-foreground py-2 text-sm font-semibold">Add testimonial</button>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading font-semibold text-lg">FAQs</h2>
        <ul className="mt-3 space-y-2">
          {faqs.map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-3 rounded-xl bg-secondary px-4 py-2.5 text-sm">
              <span>{f.q}</span>
              <button data-testid={`faq-delete-${f.id}`} onClick={() => del("faqs", f.id)} aria-label="Delete FAQ" className="text-coral-600"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
        <div className="mt-4 grid gap-2">
          <input data-testid="new-faq-q" placeholder="Question" value={newF.q} onChange={(e) => setNewF({ ...newF, q: e.target.value })} className="rounded-xl border border-input bg-background px-3 py-2 text-sm" aria-label="FAQ question" />
          <textarea data-testid="new-faq-a" placeholder="Answer" value={newF.a} onChange={(e) => setNewF({ ...newF, a: e.target.value })} className="rounded-xl border border-input bg-background px-3 py-2 text-sm" rows={2} aria-label="FAQ answer" />
          <button data-testid="new-faq-add" onClick={() => add("faqs", newF, () => setNewF({ q: "", a: "" }))}
            className="rounded-full bg-primary text-primary-foreground py-2 text-sm font-semibold">Add FAQ</button>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg">Blog posts</h2>
          {!newP && (
            <button data-testid="new-post-open" onClick={() => setNewP({})}
              className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold">New post</button>
          )}
        </div>
        <ul className="mt-3 space-y-2">
          {posts.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 rounded-xl bg-secondary px-4 py-2.5 text-sm">
              <span>{p.title} <span className="text-muted-foreground">/blog/{p.slug}</span>
                <span className="block text-xs text-muted-foreground">Written by {p.author || "Tendly Team"}{p.visuals_by ? ` · Visuals by ${p.visuals_by}` : ""}</span>
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <button data-testid={`post-edit-${p.id}`} onClick={() => setNewP(p)} aria-label="Edit post" className="text-primary"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`post-delete-${p.id}`} onClick={() => del("blog_posts", p.id)} aria-label="Delete post" className="text-coral-600"><Trash2 className="w-4 h-4" /></button>
              </span>
            </li>
          ))}
        </ul>
        {newP && <PostForm initial={newP.id ? newP : null} onDone={() => { setNewP(null); load(); }} onCancel={() => setNewP(null)} />}
      </section>
    </div>
  );
}
